import { PreferencesState } from '@abb-procure/data';
import { BooleanInput } from '@angular/cdk/coercion';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  TemplateRef,
  booleanAttribute,
  computed,
  contentChildren,
  inject,
  input,
  output,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  IsActiveMatchOptions,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { QuicklinkDirective } from 'ngx-quicklink';
import { ViewNavigationService } from '../../services/view-navigation.service';
import { MenuChild } from '../menu-child/menu-child.component';
import { Menu } from '../menu/menu.component';
import { NumberBadge } from '../number-badge/number-badge.component';

@Component({
  selector: 'procure-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
  imports: [
    MatIcon,
    RouterLink,
    RouterLinkActive,
    NgTemplateOutlet,
    MatTooltip,
    QuicklinkDirective,
    NumberBadge,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuItem {
  readonly children = contentChildren(MenuChild);

  readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly view = input<string>();
  readonly icon = input<string | TemplateRef<unknown>>();
  readonly link = input<string[]>();
  readonly fragment = input<string>();
  readonly label = input<string>();
  readonly tooltip = input<string>();
  readonly badge = input<number | null>(null);
  readonly badgeColor = input<'danger' | 'blue'>('danger');

  readonly outlined = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly primary = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly faded = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly partialMatch = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly showChildren = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  readonly secondary = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly active = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly warning = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly error = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly viewChange = output<string>();

  protected readonly itemTooltip = computed(() =>
    !this.expanded() ? this.label() : this.tooltip(),
  );

  protected readonly preferences = inject(PreferencesState);

  protected readonly expanded = computed(() =>
    !this.secondary()
      ? this.menu?.isExpanded()
      : this.preferences.isSecondaryNavigationExpanded(),
  );

  protected readonly viewNavigationService = inject(ViewNavigationService);

  protected readonly activeView = this.viewNavigationService.activeView;

  protected readonly hasActiveChild = computed(() =>
    this.children().some((child) => child.active()),
  );

  protected readonly linkActiveOptions = computed<IsActiveMatchOptions>(() => ({
    paths:
      this.partialMatch() || this.children().length > 0 ? 'subset' : 'exact',
    queryParams:
      this.children().length > 0
        ? 'ignored'
        : this.partialMatch()
          ? 'subset'
          : 'exact',
    fragment: 'ignored',
    matrixParams: 'ignored',
  }));

  protected readonly canHighlight = computed(
    () => !this.menu?.suppressActive() && !this.menu?.dimActive(),
  );

  protected readonly isDimHighlight = computed(
    () => this.menu?.dimActive() ?? false,
  );

  private readonly menu = inject(Menu, { optional: true });

  protected navigate(): void {
    const view = this.view();

    if (!view) {
      return;
    }

    this.viewNavigationService.setActiveView(view);

    const url = new URL(window.location.toString());
    url.searchParams.set('view', view);

    window.history.pushState({}, '', url);
  }

  protected isIconName(icon: unknown): icon is string {
    return typeof icon === 'string';
  }
}
